import { BrandLoader } from "@/components/brand/BrandLoader";
import { PublicLayout } from "@/components/public/PublicLayout";
import { registerPreload } from "@/lib/route-preload";
import { NotFoundPage } from "@/pages/NotFoundPage";
import { type ComponentType, type ReactNode, Suspense, lazy } from "react";
import { Route, Routes } from "react-router-dom";

/**
 * 라우트마다 청크를 나눈다. 예전엔 모든 화면(콘솔 전체·Firebase 포함)이 메인 번들 하나에
 * 실려서 게스트 챗·랜딩도 콘솔 코드를 다 받은 뒤에야 그려졌다.
 *
 * 페이지 이동은 BrowserRouter 의 v7_startTransition 으로 transition 이 되므로, 다음 청크를
 * 받는 동안에도 지금 화면이 그대로 남는다 — 아래 fallback 은 첫 진입에만 보인다.
 */
function page<K extends string>(load: () => Promise<Record<K, ComponentType>>, name: K) {
  return Object.assign(
    lazy(() => load().then((m) => ({ default: m[name] }))),
    { preload: load }
  );
}

// Public
const LandingPage = page(() => import("@/pages/LandingPage"), "LandingPage");
const UserGuidePage = page(() => import("@/pages/UserGuidePage"), "UserGuidePage");
const FaqPage = page(() => import("@/pages/FaqPage"), "FaqPage");
const TermsPage = page(() => import("@/pages/TermsPage"), "TermsPage");
const PrivacyPage = page(() => import("@/pages/PrivacyPage"), "PrivacyPage");
const GuestChatPage = page(() => import("@/pages/GuestChatPage"), "GuestChatPage");
const PropertyChatPage = page(() => import("@/pages/PropertyChatPage"), "PropertyChatPage");

// Admin
const AdminLoginPage = page(() => import("@/pages/AdminLoginPage"), "AdminLoginPage");
const InviteAcceptPage = page(() => import("@/pages/InviteAcceptPage"), "InviteAcceptPage");
const ADMIN_PAGES = {
  DashboardPage: page(() => import("@/pages/DashboardPage"), "DashboardPage"),
  ConversationsPage: page(() => import("@/pages/ConversationsPage"), "ConversationsPage"),
  RoomsPage: page(() => import("@/pages/RoomsPage"), "RoomsPage"),
  RoomBoardPage: page(() => import("@/pages/RoomBoardPage"), "RoomBoardPage"),
  RoomTypesPage: page(() => import("@/pages/RoomTypesPage"), "RoomTypesPage"),
  ReservationsPage: page(() => import("@/pages/ReservationsPage"), "ReservationsPage"),
  TimelinePage: page(() => import("@/pages/TimelinePage"), "TimelinePage"),
  GuidesPage: page(() => import("@/pages/GuidesPage"), "GuidesPage"),
  ContractsPage: page(() => import("@/pages/ContractsPage"), "ContractsPage"),
  PropertiesPage: page(() => import("@/pages/PropertiesPage"), "PropertiesPage"),
  ChannelsPage: page(() => import("@/pages/ChannelsPage"), "ChannelsPage"),
  ReportsPage: page(() => import("@/pages/ReportsPage"), "ReportsPage"),
  MembersPage: page(() => import("@/pages/MembersPage"), "MembersPage"),
  SuperAdminPage: page(() => import("@/pages/SuperAdminPage"), "SuperAdminPage"),
  OtaMappingGuidePage: page(() => import("@/pages/OtaMappingGuidePage"), "OtaMappingGuidePage"),
};

// 콘솔 셸이 뜨면 나머지 콘솔 화면을 한가할 때 미리 받아 둔다 — 메뉴 이동이 기다리지 않게.
const ProtectedAdminLayout = lazy(() =>
  import("@/components/admin/ProtectedAdminLayout").then((m) => {
    const idle = window.requestIdleCallback ?? ((cb: () => void) => window.setTimeout(cb, 1200));
    idle(() => {
      for (const p of Object.values(ADMIN_PAGES)) void p.preload();
    });
    return { default: m.ProtectedAdminLayout };
  })
);

// 공개 레이아웃이 링크에 닿을 때 다음 화면 청크를 미리 받는다(전환 애니메이션이 기다리지 않게).
registerPreload("/", LandingPage.preload);
registerPreload("/guide", UserGuidePage.preload);
registerPreload("/faq", FaqPage.preload);
registerPreload("/terms", TermsPage.preload);
registerPreload("/privacy", PrivacyPage.preload);
registerPreload("/admin/login", AdminLoginPage.preload);

// 첫 진입이 랜딩이면 라우터가 렌더하기 전에 청크를 미리 받기 시작한다.
if (typeof window !== "undefined" && window.location.pathname === "/") void LandingPage.preload();

/** 공개 페이지는 헤더·푸터를 남긴 채 본문 자리만 잡아 둔다(푸터가 올라왔다 밀려나지 않게). */
const publicPage = (el: ReactNode) => (
  <Suspense fallback={<div className="min-h-[calc(100svh-4rem)]" />}>{el}</Suspense>
);

const {
  DashboardPage,
  ConversationsPage,
  RoomsPage,
  RoomBoardPage,
  RoomTypesPage,
  ReservationsPage,
  TimelinePage,
  GuidesPage,
  ContractsPage,
  PropertiesPage,
  ChannelsPage,
  ReportsPage,
  MembersPage,
  SuperAdminPage,
  OtaMappingGuidePage,
} = ADMIN_PAGES;

export default function App() {
  return (
    <Suspense fallback={<BrandLoader fullscreen />}>
      <Routes>
        {/* Public marketing & docs */}
        <Route element={<PublicLayout />}>
          <Route path="/" element={publicPage(<LandingPage />)} />
          <Route path="/guide" element={publicPage(<UserGuidePage />)} />
          <Route path="/faq" element={publicPage(<FaqPage />)} />
          <Route path="/terms" element={publicPage(<TermsPage />)} />
          <Route path="/privacy" element={publicPage(<PrivacyPage />)} />
        </Route>
        {/* Public guest chat */}
        <Route path="/g/:token" element={<GuestChatPage />} />
        {/* Public property chat */}
        <Route path="/p/:token" element={<PropertyChatPage />} />
        {/* Admin */}
        <Route path="/admin/login" element={<AdminLoginPage />} />
        <Route path="/admin/invite/:token" element={<InviteAcceptPage />} />
        <Route path="/admin" element={<ProtectedAdminLayout />}>
          <Route index element={<DashboardPage />} />
          <Route path="conversations" element={<ConversationsPage />} />
          <Route path="rooms" element={<RoomsPage />} />
          <Route path="rooms/board" element={<RoomBoardPage />} />
          <Route path="room-types" element={<RoomTypesPage />} />
          <Route path="reservations" element={<ReservationsPage />} />
          <Route path="timeline" element={<TimelinePage />} />
          <Route path="guides" element={<GuidesPage />} />
          <Route path="contracts" element={<ContractsPage />} />
          <Route path="properties" element={<PropertiesPage />} />
          <Route path="channels" element={<ChannelsPage />} />
          <Route path="reports" element={<ReportsPage />} />
          <Route path="members" element={<MembersPage />} />
          <Route path="super" element={<SuperAdminPage />} />
          <Route path="super/ota-guide" element={<OtaMappingGuidePage />} />
        </Route>

        {/* Fallback */}
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </Suspense>
  );
}
